import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type Row,
} from '@tanstack/react-table';
import {
  Check,
  CheckCircle2,
  Copy,
  Layers,
  MoreVertical,
  RefreshCw,
  Search,
  ShieldQuestion,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { useDuplicates, useResolveDuplicates, duplicateKeys } from '@/hooks/useJobs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { jobsApi } from '@/lib/api/jobs.api';
import { cn } from '@/lib/utils';
import { DuplicateStatus, type DuplicateGroup, type DuplicateFilters, type Job } from '@repo/types';

export const Route = createFileRoute('/admin/jobs/duplicates')({
  component: DuplicatesPage,
});

function similarityMeta(pct: number) {
  if (pct >= 90)
    return {
      badge: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400',
      bar: 'bg-red-500',
    };
  if (pct >= 80)
    return {
      badge: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400',
      bar: 'bg-amber-500',
    };
  return {
    badge: 'border-border bg-muted text-muted-foreground',
    bar: 'bg-muted-foreground/50',
  };
}

const STATUS_META: Record<string, { label: string; className: string }> = {
  unresolved: {
    label: 'Unresolved',
    className: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  resolved: {
    label: 'Resolved',
    className: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  not_duplicate: {
    label: 'Not duplicate',
    className: 'border-border bg-muted text-muted-foreground',
  },
};

function DuplicateStatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? STATUS_META.unresolved;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        meta.className
      )}
    >
      {meta.label}
    </span>
  );
}

function formatSalary(job: Job) {
  if (!job.salaryMin && !job.salaryMax) return 'Not specified';
  const fmt = (n: number) => {
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: job.salaryCurrency || 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(n);
    } catch {
      return `${n}`;
    }
  };
  if (job.salaryMin && job.salaryMax) return `${fmt(job.salaryMin)} – ${fmt(job.salaryMax)}`;
  return fmt((job.salaryMin || job.salaryMax) as number);
}

function CompareField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-sm">{children || '—'}</div>
    </div>
  );
}

/** Inline side-by-side comparison shown when a group row is expanded. */
function ComparisonPanel({
  group,
  onKeepOne,
  onKeepAll,
  isResolving,
}: {
  group: DuplicateGroup;
  onKeepOne: (jobId: string) => void;
  onKeepAll: () => void;
  isResolving: boolean;
}) {
  return (
    <div className="space-y-4 px-6 py-5">
      <div className="grid gap-4 md:grid-cols-2">
        {group.jobs.map((job) => (
          <div key={job.id} className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Link
                  to="/admin/jobs/$id/details"
                  params={{ id: job.id }}
                  className="line-clamp-2 font-medium hover:text-primary hover:underline"
                >
                  {job.title}
                </Link>
                <div className="text-xs text-muted-foreground">
                  #{job.jobKey} · {job.company}
                </div>
              </div>
              <Badge variant="secondary" className="capitalize">
                {job.status}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              <CompareField label="Location">{job.location}</CompareField>
              <CompareField label="Industry">{job.industry}</CompareField>
              <CompareField label="Job Type">
                <span className="capitalize">{job.jobType}</span>
              </CompareField>
              <CompareField label="Work Mode">
                <span className="capitalize">{job.workMode}</span>
              </CompareField>
              <CompareField label="Salary">{formatSalary(job)}</CompareField>
              <CompareField label="Views">{job.viewCount}</CompareField>
              <CompareField label="Posted by">{job.postedBy?.name}</CompareField>
              <CompareField label="Created">
                {format(new Date(job.createdAt), 'MMM d, yyyy')}
              </CompareField>
            </div>
            {job.skills?.length > 0 && (
              <div className="mt-3">
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Skills</div>
                <div className="flex flex-wrap gap-1">
                  {job.skills.map((skill) => (
                    <Badge key={skill} variant="outline" className="text-xs font-normal">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            <Button
              size="sm"
              className="mt-4 w-full"
              disabled={isResolving}
              onClick={() => onKeepOne(job.id)}
            >
              <Check className="size-4" />
              Keep #{job.jobKey} only
            </Button>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button variant="outline" size="sm" disabled={isResolving} onClick={onKeepAll}>
          <CheckCircle2 className="size-4" />
          Keep all — not a duplicate
        </Button>
      </div>
    </div>
  );
}

function DuplicatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>(DuplicateStatus.Unresolved);
  const [minSimilarity, setMinSimilarity] = useState('0.75');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filters: DuplicateFilters = useMemo(
    () => ({
      minSimilarity: Number(minSimilarity),
      status: statusFilter === 'all' ? 'all' : (statusFilter as DuplicateStatus),
    }),
    [minSimilarity, statusFilter]
  );

  const { data, isLoading, isFetching, error, refetch } = useDuplicates(filters);
  const groups = useMemo(() => data?.duplicateGroups ?? [], [data]);

  const resolveMutation = useResolveDuplicates();
  const notDuplicateMutation = useMutation({
    mutationFn: (groupId: string) => jobsApi.markNotDuplicate(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: duplicateKeys.lists() });
      toast.success('Marked as not a duplicate');
    },
    onError: () => toast.error('Failed to update status'),
  });
  const isResolving = resolveMutation.isPending || notDuplicateMutation.isPending;

  const keepOne = (groupId: string, jobId: string) =>
    resolveMutation.mutate({ groupId, action: 'keep', keptJobId: jobId, comments: 'Kept single job' });
  const keepAll = (groupId: string) => notDuplicateMutation.mutate(groupId);

  // Guarantee a visible spin even when the refetch resolves instantly (cached).
  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    window.setTimeout(() => setIsRefreshing(false), 600);
  };
  const refreshing = isRefreshing || isFetching;

  // Job selector search
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const [selectorJobs, setSelectorJobs] = useState<Job[]>([]);
  useEffect(() => {
    if (!showJobSelector) return;
    let active = true;
    jobsApi
      .getJobs({ search: debouncedSearch || undefined })
      .then((res) => {
        if (active) setSelectorJobs(res.jobs);
      })
      .catch(() => toast.error('Failed to search jobs'));
    return () => {
      active = false;
    };
  }, [showJobSelector, debouncedSearch]);

  // Derived summary
  const summary = useMemo(() => {
    const high = groups.filter((g) => g.similarity >= 0.9).length;
    const avg = groups.length
      ? Math.round((groups.reduce((s, g) => s + g.similarity, 0) / groups.length) * 100)
      : 0;
    return { total: groups.length, high, avg };
  }, [groups]);

  const columns: ColumnDef<DuplicateGroup>[] = [
    {
      id: 'expander',
      header: () => null,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
          onClick={row.getToggleExpandedHandler()}
        >
          <Copy className="size-3.5" />
          {row.getIsExpanded() ? 'Hide' : 'Compare'}
        </Button>
      ),
    },
    {
      id: 'jobs',
      header: 'Duplicate Jobs',
      cell: ({ row }) => (
        <div className="max-w-[440px] space-y-2">
          {row.original.jobs.map((job) => (
            <div key={job.id} className="min-w-0">
              <Link
                to="/admin/jobs/$id/details"
                params={{ id: job.id }}
                className="block truncate font-medium hover:text-primary hover:underline"
              >
                {job.title}
              </Link>
              <div className="truncate text-xs text-muted-foreground">
                #{job.jobKey} · {job.company} · {job.location}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: 'similarity',
      header: 'Similarity',
      cell: ({ row }) => {
        const pct = Math.round(row.original.similarity * 100);
        const meta = similarityMeta(pct);
        return (
          <div className="w-28 space-y-1.5">
            <span
              className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold',
                meta.badge
              )}
            >
              {pct}%
            </span>
            <Progress value={pct} className="h-1.5 bg-muted" indicatorClassName={meta.bar} />
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <DuplicateStatusBadge status={row.original.status} />,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const group = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => row.toggleExpanded()}>
                <Copy className="size-4" />
                {row.getIsExpanded() ? 'Hide comparison' : 'Compare'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {group.jobs.map((job) => (
                <DropdownMenuItem key={job.id} onClick={() => keepOne(group.id, job.id)}>
                  <Check className="size-4" />
                  Keep #{job.jobKey} only
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onClick={() => keepAll(group.id)}>
                <CheckCircle2 className="size-4" />
                Keep all — not duplicate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: groups,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getRowId: (row) => row.id,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="space-y-6 p-6 duration-300 animate-in fade-in">
      <PageHeader
        title="Duplicate Jobs"
        description="Review potential duplicates and resolve them by keeping one or marking as unique."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn('size-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => setShowJobSelector(true)}>
              <Search className="size-4" />
              Find duplicates for a job
            </Button>
          </>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Duplicate Groups" value={String(summary.total)} icon={Layers} />
        <StatCard label="High Similarity (≥90%)" value={String(summary.high)} icon={AlertCircle} />
        <StatCard label="Avg Similarity" value={`${summary.avg}%`} icon={ShieldQuestion} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DuplicateStatus.Unresolved}>Unresolved</SelectItem>
            <SelectItem value={DuplicateStatus.Resolved}>Resolved</SelectItem>
            <SelectItem value={DuplicateStatus.NotDuplicate}>Not duplicate</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={minSimilarity} onValueChange={setMinSimilarity}>
          <SelectTrigger className="w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0.75">Similarity ≥ 75%</SelectItem>
            <SelectItem value="0.85">Similarity ≥ 85%</SelectItem>
            <SelectItem value="0.9">Similarity ≥ 90%</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load duplicates.'}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={3} />
      ) : (
        <DataTable
          table={table}
          renderSubComponent={(row: Row<DuplicateGroup>) => (
            <ComparisonPanel
              group={row.original}
              isResolving={isResolving}
              onKeepOne={(jobId) => keepOne(row.original.id, jobId)}
              onKeepAll={() => keepAll(row.original.id)}
            />
          )}
          emptyState={
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <CheckCircle2 className="size-6 text-emerald-600 dark:text-emerald-500" />
                </EmptyMedia>
                <EmptyTitle>No duplicates found</EmptyTitle>
                <EmptyDescription>
                  Nothing to review at this similarity threshold — all jobs look unique.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          }
        />
      )}

      {/* Job selector */}
      <Dialog open={showJobSelector} onOpenChange={setShowJobSelector}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Find duplicates for a job</DialogTitle>
            <DialogDescription>Search and select a job to find potential duplicates.</DialogDescription>
          </DialogHeader>
          <Command className="rounded-lg border" shouldFilter={false}>
            <CommandInput
              placeholder="Search by job title, company…"
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>No jobs found.</CommandEmpty>
              <CommandGroup heading="Jobs">
                {selectorJobs.slice(0, 10).map((job) => (
                  <CommandItem
                    key={job.id}
                    value={job.id}
                    onSelect={() => {
                      setShowJobSelector(false);
                      navigate({ to: '/admin/jobs/$id/duplicates', params: { id: job.id } });
                    }}
                    className="flex items-start gap-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono text-xs">
                          #{job.jobKey}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {job.status}
                        </Badge>
                      </div>
                      <p className="truncate font-medium">{job.title}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {job.company} · {job.location}
                      </p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  );
}
